import {useMutation, useQuery} from '@tanstack/react-query'

import {toast} from 'react-toastify'
import useRequestParams from '../hook/request'
import {API, CAMPAIGN_API, CRM_API} from '../domain'

const apiCampaignProduct = `${CAMPAIGN_API}/CampaignProduct`
const apiExcludeCampaignProduct = `${CAMPAIGN_API}/CampaignExcludeProduct`
const apiPriceCluster = `${CAMPAIGN_API}/CampaignPriceCluster`
const apiCampaign = `${CAMPAIGN_API}/CampaignPoint`
const apiCampaignFile = `${CAMPAIGN_API}/CampaignFile`

export function useCheckToken() {
  const {auth, headers, signOut} = useRequestParams()

  const userID = auth() ? auth().id : null
  const language = headers.Language

  return useQuery(
    ['checkToken'],
    async () => {
      if (!userID) return false

      const Response = await fetch(`${API}/Token/TokenCheck`, {
        method: 'POST',
        headers,
        body: JSON.stringify({userID, language}),
      })

      if (Response.status === 401) {
        signOut()
      }

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      retry: false,
    }
  )
}

export function useUploadFile() {
  const {headers, errorMessage, errors} = useRequestParams()

  return useMutation(['uploadFile'], async (file: File, FolderName: string = 'Product') => {
    // Max 3MB file size
    const maxFileSize = 3 * 1024 * 1024

    if (file.size > maxFileSize) {
      toast.error('File size should be less than 3MB')

      return false
    }

    const allowedAspectRatio = 1

    const newImage = new Image()
    newImage.src = URL.createObjectURL(file)
    /*     const isValidRatio = await new Promise((resolve, reject) => {
      newImage.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = newImage.width
        canvas.height = newImage.height

        if (newImage.width / newImage.height !== allowedAspectRatio) {
          toast.error('Image aspect ratio should be 1:1')
          resolve(false)
        } else {
          resolve(true)
        }
      }
    })

    if (!isValidRatio) {
      return false
    } */

    const formData = new FormData()
    formData.append('File', file)
    formData.append('FolderName', FolderName)

    const Response = await fetch(`${CRM_API}/File/FileUpload`, {
      method: 'POST',
      headers: {
        Authorization: headers.Authorization,
      },
      body: formData,
    })

    const Data = await Response.json()

    if (!Response.ok || !Data.result.isSuccess) {
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
        theme: 'light',
      })

      return false
    }

    toast.success(errors.file.upload_success, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      theme: 'light',
    })

    return Data
  })
}

export function useDeleteFile() {
  const {headers, errorMessage, errors} = useRequestParams()

  return useMutation(
    ['deleteFile'],
    async (file: any) => {
      const Response = await fetch(`${API}/File/DeleteFile`, {
        method: 'POST',
        headers,
        body: JSON.stringify(file),
      })

      const Data = await Response.json()

      if (!Response.ok || !Data.responseResult.result) {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })

        return
      }

      toast.success(errors.file.delete_success, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
        theme: 'light',
      })

      return Data.response
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errors.file.delete_error, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export const createCampaignPoint = async (
  userID: string,
  headers: any,
  campaignID: string,
  fixedValue: number,
  pointsToBeEarned: number,
  amountToBeSpent: number
) => {
  const campaignPoint = {
    accrualPercentage: 0,
    fixedValue,
    redemptionValue: 1,
    activationType: 1,
    deferredTime: 'string',
    everyAfterAccrualMonth: [0],
    everyAfterAccrualWeek: [0],
    everyAfterAccrualDay: [0],
    everyAfterAccrualTime: 'string',
    expirationType: 0,
    expirationValue: 0,
    expirationTime: '23:99',
    expirationDate: '2100-05-21T13:25:23.237Z',
    pointsToBeEarned,
    amountToBeSpent,
    multiplier: 0,
  }

  const reqBodyPoint = {
    userID,
    campaignID,
    campaignPoint,
  }

  const ResponsePoint = await fetch(`${apiCampaign}/CreateCampaignPoint`, {
    method: 'POST',
    headers,
    body: JSON.stringify(reqBodyPoint),
  })

  const DataPoint = await ResponsePoint.json()

  return DataPoint
}

export const createCampaignPriceCluster = async (
  userID: string,
  headers: any,
  campaignID: string,
  priceCluster: any
) => {
  if (priceCluster.length === 0) {
    return
  }

  const reqBodyPriceCluster = {
    userID: userID,
    campaignID,
    campaignPriceClusterList: priceCluster,
  }

  const ResponsePriceCluster = await fetch(`${apiPriceCluster}/CreateCampaignPriceCluster`, {
    method: 'POST',
    headers,
    body: JSON.stringify(reqBodyPriceCluster),
  })

  const DataPriceCluster = await ResponsePriceCluster.json()

  return DataPriceCluster
}

export const createCampaignProduct = async (
  userID: string,
  headers: any,
  campaignID: string,
  productIDList: string[]
) => {
  if (productIDList.length === 0) {
    return
  }

  const reqBodyProduct = {
    userID,
    campaignID,
    productIDList,
  }

  const ResponseProduct = await fetch(`${apiCampaignProduct}/CreateCampaignProduct`, {
    method: 'POST',
    headers,
    body: JSON.stringify(reqBodyProduct),
  })

  const DataProduct = await ResponseProduct.json()

  return DataProduct
}

export const createCampaignFile = async (
  userID: string,
  headers: any,
  campaignID: string,
  fileIDList: {id: string; name: string}[]
) => {
  if (fileIDList.length === 0) {
    return
  }

  const ids = fileIDList.map((file) => file.id)

  const reqBodyFile = {
    userID,
    campaignID,
    fileIDList: ids,
  }

  const ResponseFile = await fetch(`${apiCampaignFile}/CreateCampaignFile`, {
    method: 'POST',
    headers,
    body: JSON.stringify(reqBodyFile),
  })

  const DataFile = await ResponseFile.json()

  return DataFile
}

export const deleteCampaignFile = async (
  userID: string,
  headers: any,
  campaignID: string,
  fileIDList: string[]
) => {
  if (fileIDList.length === 0) {
    return
  }

  const reqBodyFile = {
    userID,
    campaignID,
    fileIDList,
  }

  const ResponseFile = await fetch(`${apiCampaignFile}/DeleteCampaignFile`, {
    method: 'POST',
    headers,
    body: JSON.stringify(reqBodyFile),
  })

  const DataFile = await ResponseFile.json()

  return DataFile
}

export const deleteCampaignProduct = async (
  userID: string,
  headers: any,
  campaignID: string,
  productIDList: string[]
) => {
  if (productIDList.length === 0) {
    return
  }

  const reqBodyProduct = {
    userID,
    campaignID,
    productIDList,
  }

  const ResponseProduct = await fetch(`${apiCampaignProduct}/DeleteCampaignProduct`, {
    method: 'POST',
    headers,
    body: JSON.stringify(reqBodyProduct),
  })

  const DataProduct = await ResponseProduct.json()

  return DataProduct
}

export const deleteExcludeCampaignProduct = async (
  userID: string,
  headers: any,
  campaignID: string,
  productIDList: string[]
) => {
  if (productIDList.length === 0) {
    return
  }

  const reqBodyProduct = {
    userID,
    campaignID,
    productIDList,
  }

  const ResponseProduct = await fetch(`${apiExcludeCampaignProduct}/DeleteCampaignExcludeProduct`, {
    method: 'POST',
    headers,
    body: JSON.stringify(reqBodyProduct),
  })

  const DataProduct = await ResponseProduct.json()

  return DataProduct
}
